export type SvgDimensions = {
  width: number;
  height: number;
};

const DEFAULT_DIMENSIONS: SvgDimensions = { width: 800, height: 600 };

export function parseSvgDimensions(svg: string): SvgDimensions {
  const viewBoxMatch = svg.match(/viewBox\s*=\s*["']\s*0\s+0\s+([\d.]+)\s+([\d.]+)\s*["']/i);
  if (viewBoxMatch) {
    const width = Number.parseFloat(viewBoxMatch[1]);
    const height = Number.parseFloat(viewBoxMatch[2]);
    if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
      return { width, height };
    }
  }

  const widthMatch = svg.match(/\bwidth\s*=\s*["']([\d.]+)/i);
  const heightMatch = svg.match(/\bheight\s*=\s*["']([\d.]+)/i);
  const width = widthMatch ? Number.parseFloat(widthMatch[1]) : 0;
  const height = heightMatch ? Number.parseFloat(heightMatch[1]) : 0;
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return { width, height };
  }

  return DEFAULT_DIMENSIONS;
}

function ensureSvgNamespace(svg: string): string {
  if (/xmlns\s*=/i.test(svg)) return svg;
  return svg.replace(/<svg\b/i, '<svg xmlns="http://www.w3.org/2000/svg"');
}

async function svgToPngBlob(svg: string): Promise<Blob> {
  const { width, height } = parseSvgDimensions(svg);
  const normalizedSvg = ensureSvgNamespace(svg);
  const svgBlob = new Blob([normalizedSvg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not render diagram image.'));
      img.src = url;
    });

    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(width * scale);
    canvas.height = Math.ceil(height * scale);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not prepare diagram image.');

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/png');
    });
    if (!blob) throw new Error('Could not create diagram image.');

    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function copySvgFigureToClipboard(svg: string): Promise<void> {
  const trimmed = svg?.trim();
  if (!trimmed) throw new Error('Nothing to copy.');

  if (typeof navigator !== 'undefined' && navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    try {
      const pngBlob = await svgToPngBlob(trimmed);
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
      return;
    } catch {
      // Fall through to SVG text copy.
    }
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(trimmed);
    return;
  }

  throw new Error('Copy is not supported in this browser.');
}
